import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';
import { FiGrid, FiDownload, FiCopy, FiCheck } from 'react-icons/fi';

const QRContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const QRHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const QRTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const QRCodeCanvas = styled.canvas`
  border: 2px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
  max-width: 100%;
  height: auto;
`;

const QRInfo = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.4;
`;

const QRCodeData = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 0.75rem;
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  color: var(--text-primary);
  word-break: break-all;
  max-width: 100%;
  margin: 0.5rem 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: var(--bg-secondary);
    border-color: var(--primary-color);
    transform: translateY(-1px);
  }
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover {
      background: var(--primary-hover);
    }
  }
  
  &.success {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border-color: var(--success-color);
    
    &:hover {
      background: rgba(104, 211, 145, 0.2);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid var(--primary-color);
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  color: var(--error-color);
  padding: 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  text-align: center;
`;

function QRCodeGenerator({ 
  data, 
  title = "Attendance QR Code", 
  description = "Scan this QR code to check in for attendance",
  size = 200,
  showData = false,
  showActions = true 
}) {
  const [qrCodeDataURL, setQrCodeDataURL] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const generateQRCode = useCallback(async () => {
    if (!data) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const options = {
        width: size,
        margin: 2,
        color: {
          // QR library expects explicit color values (CSS vars are not supported here)
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      };
      
      const dataURL = await QRCode.toDataURL(data, options);
      setQrCodeDataURL(dataURL);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  }, [data, size]);

  useEffect(() => {
    if (data) {
      generateQRCode();
    }
  }, [data, size, generateQRCode]);

  const downloadQRCode = () => {
    if (!qrCodeDataURL) return;
    
    const link = document.createElement('a');
    link.download = `attendance-qr-${Date.now()}.png`;
    link.href = qrCodeDataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = async () => {
    if (!data) return;
    
    try {
      await navigator.clipboard.writeText(data);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  if (error) {
    return (
      <QRContainer
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <ErrorMessage>{error}</ErrorMessage>
      </QRContainer>
    );
  }

  return (
    <QRContainer
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
    >
      <QRHeader>
        <FiGrid size={20} color="var(--primary-color)" />
        <QRTitle>{title}</QRTitle>
      </QRHeader>
      
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <LoadingSpinner />
          <QRInfo>Generating QR code...</QRInfo>
        </div>
      ) : qrCodeDataURL ? (
        <>
          <QRCodeCanvas
            ref={(canvas) => {
              if (canvas && qrCodeDataURL) {
                const ctx = canvas.getContext('2d');
                const img = new Image();
                img.onload = () => {
                  canvas.width = size;
                  canvas.height = size;
                  ctx.drawImage(img, 0, 0, size, size);
                };
                img.src = qrCodeDataURL;
              }
            }}
          />
          
          <QRInfo>{description}</QRInfo>
          
          {showData && (
            <QRCodeData>{data}</QRCodeData>
          )}
          
          {showActions && (
            <ActionButtons>
              <ActionButton onClick={downloadQRCode}>
                <FiDownload size={16} />
                Download
              </ActionButton>
              <ActionButton 
                onClick={copyToClipboard}
                className={copied ? 'success' : ''}
              >
                {copied ? <FiCheck size={16} /> : <FiCopy size={16} />}
                {copied ? 'Copied!' : 'Copy Link'}
              </ActionButton>
            </ActionButtons>
          )}
        </>
      ) : (
        <QRInfo>No data provided for QR code generation</QRInfo>
      )}
    </QRContainer>
  );
}

export default QRCodeGenerator;
